import axios from "axios";
import { getConfig } from "../app/config";
import { Count, HasId } from "./Types";

export interface ListDto<T> {
  items: T[];
  total: number;
}
export interface PagedList<T> {
  items: T[];
  total: number;
  page: number;
  totalPages: number;
}

export enum ErrorApiType {
  None,
  Warning,
  Error,
  Validation
}

export interface ErrorDto {
  type: ErrorApiType;
  key?: string;
  field?: string;
  message: string;
}
export interface ServerError {
  traceId: string;
  errors: ErrorDto[];
}

const config = getConfig();

const apiUrl: string = config.ServerUrl || "/api";
axios.defaults.headers.common["TenantId"] = config.TenantId || "";

const defaultPageSize = 20;

export function pageFromList<T>(list: ListDto<T>, page: number, pageSize: number): PagedList<T> {
  return {
    items: list.items,
    total: list.total,
    page: page,
    totalPages: Math.ceil(list.total / pageSize)
  };
}

export async function get<T>(name: string) {
  const url = `${apiUrl}/${name}`;
  const { data } = await axios.get<T>(url);
  return data;
}

export async function getBlob(url: string) {
  const { data } = await axios.get(`${apiUrl}/${url}`, {
    responseType: "blob"
  });
  return data;
}

export async function getItem<T>(name: string, id: string) {
  const url = `${apiUrl}/${name}/${id}`;
  const { data } = await axios.get<T>(url);
  return data;
}

export async function getItems<T>(name: string): Promise<T[]> {
  const url = `${apiUrl}/${name}`;

  try {
    const itemsResponse = await axios.get<T[]>(url);
    return itemsResponse.data;
  } catch (err: any) {
    throw err.response.data?.errors[0]?.message ?? err.response.data;
  }
}

export async function getPagedItems<T>(
  name: string,
  page = 1,
  pageSize = defaultPageSize,
  searchString: string = "",
  signal: AbortSignal | undefined = undefined
): Promise<PagedList<T>> {
  const url = `${apiUrl}/${name}?page=${page}&pageSize=${pageSize}&q=${searchString}`;

  try {
    const itemsResponse = await axios.get<ListDto<T>>(url, { signal: signal });
    return pageFromList(itemsResponse.data, page, pageSize);
  } catch (err: any) {
    throw err.response.data?.errors[0]?.message ?? err.response.data;
  }
}

export async function getQueryItems<T>(
  name: string,
  query: string,
  signal: AbortSignal | undefined = undefined
): Promise<T[]> {
  const url = `${apiUrl}/${name}${query}`;

  try {
    const itemsResponse = await axios.get<T[]>(url, { signal: signal });
    return itemsResponse.data;
  } catch (err: any) {
    throw err.response.data?.errors[0]?.message ?? err.response.data;
  }
}

export async function getList<T>(path: string): Promise<T[]> {
  try {
    return (await axios.get<T[]>(`${apiUrl}/${path}`)).data;
  } catch (err: any) {
    throw err.response.data?.errors[0]?.message ?? err.response.data;
  }
}

export async function getItemCount(name: string) {
  const url = `${apiUrl}/${name}/Count`;
  const { data } = await axios.get<Count>(url);
  return data;
}

export async function updateItem<T extends HasId, R>(name: string, item: T) {
  await axios.put<R>(`${apiUrl}/${name}/${item.id}`, item).catch((err: any) => {
    throw err.response.data?.errors[0]?.message ?? err.response.data;
  });
}

export async function upload<R>(path: string, body: FormData) {
  let response = await axios
    .post<R>(`${apiUrl}/${path}`, body, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    })
    .catch((err: any) => {
      throw err.response.data?.errors[0]?.message ?? err.response.data;
    });
  return response.data;
}

export async function create<T, R>(path: string, body?: T) {
  let response = await axios.post<R>(`${apiUrl}/${path}`, body).catch((err: any) => {
    throw err.response.data?.errors[0]?.message ?? err.response.data;
  });
  return response.data;
}

export async function post<T>(path: string, body: T) {
  await axios.post(`${apiUrl}/${path}`, body).catch((err: any) => {
    throw err.response.data?.errors[0]?.message ?? err.response.data;
  });
}

export async function postAny<T>(path: string, body: T) {
  let response = await axios.post(`${apiUrl}/${path}`, body).catch((err: any) => {
    throw err.response.data?.errors[0]?.message ?? err.response.data;
  });

  return response.data;
}

export async function emptyPost(path: string) {
  await axios.post(`${apiUrl}/${path}`, {}).catch((err: any) => {
    throw err.response.data?.errors[0]?.message ?? err.response.data;
  });
}

export async function emptyPostWithResponse<T>(path: string) {
  let response = await axios.post<T>(`${apiUrl}/${path}`, {}).catch((err: any) => {
    throw err.response.data?.errors[0]?.message ?? err.response.data;
  });
  return response.data;
}

export async function update<T>(path: string, item: T) {
  let response = await axios.put(`${apiUrl}/${path}`, item).catch((err: any) => {
    throw err.response.data?.errors[0]?.message ?? err.response.data;
  });

  return response.data;
}

export async function remove(path: string) {
  let response = await axios.delete(`${apiUrl}/${path}`).catch((err: any) => {
    throw err.response.data?.errors[0]?.message ?? err.response.data;
  });

  return response.data;
}
